import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Bell, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { getLoginUrl } from "@/const";

export default function NotificationSettings() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [preferences, setPreferences] = useState({
    enablePush: true,
    enableNewDraw: true,
    enablePredictionMatch: true,
    enableSavedNumberMatch: true,
    enableDailySummary: true,
    enableSystemAlerts: true,
  });

  // Fetch preferences
  const { data: prefsData, isLoading } = trpc.notifications.getPreferences.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  // Update preferences mutation
  const updateMutation = trpc.notifications.updatePreferences.useMutation({
    onSuccess: () => {
      toast.success("Notification preferences updated!");
    },
    onError: () => {
      toast.error("Failed to update preferences");
    },
  });

  // Load preferences from server
  useEffect(() => {
    if (prefsData) {
      setPreferences({
        enablePush: prefsData.enablePush === 1,
        enableNewDraw: prefsData.enableNewDraw === 1,
        enablePredictionMatch: prefsData.enablePredictionMatch === 1,
        enableSavedNumberMatch: prefsData.enableSavedNumberMatch === 1,
        enableDailySummary: prefsData.enableDailySummary === 1,
        enableSystemAlerts: prefsData.enableSystemAlerts === 1,
      });
    }
  }, [prefsData]);

  const handleToggle = (key: keyof typeof preferences) => {
    const newValue = !preferences[key];
    setPreferences((prev) => ({ ...prev, [key]: newValue }));

    // Save to server
    updateMutation.mutate({
      [key]: newValue ? 1 : 0,
    });
  };

  const requestBrowserPermission = async () => {
    if ("Notification" in window) {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        toast.success("Browser notifications enabled!");
        handleToggle("enablePush");
      } else {
        toast.error("Browser notifications denied");
      }
    } else {
      toast.error("Browser notifications not supported");
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Login Required</CardTitle>
            <CardDescription>You need to be logged in to manage notification settings</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <a href={getLoginUrl()}>Login</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="container max-w-2xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-amber-300 bg-clip-text text-transparent mb-2">
            Notification Settings
          </h1>
          <p className="text-muted-foreground">
            Manage how you receive notifications about lottery draws and predictions
          </p>
        </div>

        <div className="space-y-6">
          {/* Browser Push Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Browser Push Notifications
              </CardTitle>
              <CardDescription>
                Receive real-time notifications even when the app is closed
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {Notification.permission === "default" ? (
                <Button onClick={requestBrowserPermission} className="w-full">
                  Enable Browser Notifications
                </Button>
              ) : Notification.permission === "denied" ? (
                <div className="text-sm text-muted-foreground bg-secondary p-4 rounded-lg">
                  Browser notifications are blocked. Please enable them in your browser settings.
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <Label htmlFor="enablePush" className="cursor-pointer">
                    Push Notifications
                  </Label>
                  <Switch
                    id="enablePush"
                    checked={preferences.enablePush}
                    onCheckedChange={() => handleToggle("enablePush")}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notification Types */}
          <Card>
            <CardHeader>
              <CardTitle>Notification Types</CardTitle>
              <CardDescription>Choose which events you want to be notified about</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="enableNewDraw" className="cursor-pointer font-medium">
                    🎰 New Lottery Draws
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Get notified when new lottery results are available
                  </p>
                </div>
                <Switch
                  id="enableNewDraw"
                  checked={preferences.enableNewDraw}
                  onCheckedChange={() => handleToggle("enableNewDraw")}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="enablePredictionMatch" className="cursor-pointer font-medium">
                    🎯 Prediction Matches
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Get notified when your predictions match winning numbers
                  </p>
                </div>
                <Switch
                  id="enablePredictionMatch"
                  checked={preferences.enablePredictionMatch}
                  onCheckedChange={() => handleToggle("enablePredictionMatch")}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="enableSavedNumberMatch" className="cursor-pointer font-medium">
                    ⭐ Saved Number Matches
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Get notified when your saved numbers appear in draws
                  </p>
                </div>
                <Switch
                  id="enableSavedNumberMatch"
                  checked={preferences.enableSavedNumberMatch}
                  onCheckedChange={() => handleToggle("enableSavedNumberMatch")}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="enableDailySummary" className="cursor-pointer font-medium">
                    📊 Daily Statistics Summary
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Receive daily summaries of lottery statistics and trends
                  </p>
                </div>
                <Switch
                  id="enableDailySummary"
                  checked={preferences.enableDailySummary}
                  onCheckedChange={() => handleToggle("enableDailySummary")}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="enableSystemAlerts" className="cursor-pointer font-medium">
                    ⚠️ System Alerts
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Important updates and maintenance notifications
                  </p>
                </div>
                <Switch
                  id="enableSystemAlerts"
                  checked={preferences.enableSystemAlerts}
                  onCheckedChange={() => handleToggle("enableSystemAlerts")}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
